import React, { useContext, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal';
import Menu from 'components/Menu/Menu';
import Toast from 'components/Toast/Toast';
import ClientContext from 'Customers/components/detailsModal/ClientContext';
import StatusChip from 'Customers/components/StatusChip/StatusChip';
import {
  useMutationSuspendCustomer,
  useMutationActivateCustomer,
  useMutationArchiveCustomer,
  useMutationRetryFailedCustomer,
  useMutationCancelTenantSubscription,
} from 'Customers/hooks/useCustomers';
import useSubscriptionSync from 'Customers/hooks/useSubscriptionSync';
import useToast from 'hooks/useToast';

import { getAccountType, getAccountOptions } from '../../utility';
import AddAdminModal from '../AddAdminModal/AddAdminModal';
import ExtendDemoModal from '../ExtendDemoModal/ExtendDemoModal';

const CustomerAccountsTableRow = ({
  accountType,
  subdomain,
  fullDomain,
  status,
  id,
  name,
  trialEnd,
  users,
  hasPaymentConfigured,
  onSelectRow,
}) => {
  const queryClient = useQueryClient();
  const { handleShowDetailsModal, handleShowDetailsCustomer } = useContext(ClientContext);
  const account = { accountType, subdomain, status, id, hasPaymentConfigured };
  const [addAdminModal, setAddAdminModal] = useState(false);
  const [extendDemoModal, setExtendDemoModal] = useState(false);
  const [loadingAction, setLoadingAction] = useState(null);
  const { toast, showSuccess, showError, hideToast } = useToast();
  const [archiveConfirmation, setArchiveConfirmation] = useState(false);

  const { mutate: suspendCustomer } = useMutationSuspendCustomer();
  const { mutate: archiveCustomer } = useMutationArchiveCustomer();
  const { mutate: activateCustomer } = useMutationActivateCustomer();
  const { mutate: retryFailedCustomer } = useMutationRetryFailedCustomer();
  const { mutate: cancelSubscription } = useMutationCancelTenantSubscription();

  // Use the shared sync hook
  const syncHook = useSubscriptionSync(id, {
    onSuccess: () => {
      // Additional success handling if needed
    },
    onError: () => {
      // Additional error handling if needed
    },
  });

  const accountOptions = getAccountOptions(status, accountType);

  const handleSuccess = async (message) => {
    await queryClient.invalidateQueries(['customersAccounts', id]);
    showSuccess(message || 'Action completed successfully');
  };

  const handleError = (error, action) => {
    showError(error?.response?.data?.message || `Failed to ${action}`);
  };

  const handleShowDetails = () => {
    onSelectRow(account);
    const shouldShowDetails =
      status !== 'ACTIVE' &&
      status !== 'PENDING_SUSPENSION' &&
      status !== 'SUSPENDED' &&
      status !== 'CANCELLED' &&
      status !== 'ARCHIVED';
    if (status !== 'PENDING') {
      if (shouldShowDetails) handleShowDetailsCustomer();
      else if (!shouldShowDetails) handleShowDetailsModal();
    }
  };

  const handleAction = (action) => {
    if (loadingAction) return; // Prevent multiple actions

    switch (action) {
      case 'Suspend':
        setLoadingAction('Suspend');
        suspendCustomer(id, {
          onSuccess: () => {
            setLoadingAction(null);
            handleSuccess('Customer suspended successfully');
          },
          onError: (error) => {
            setLoadingAction(null);
            handleError(error, 'suspend customer');
          },
        });
        break;
      case 'SyncSubscription':
        syncHook.syncCustomerSubscription();
        break;
      case 'AddAdmin':
        setAddAdminModal(true);
        break;
      case 'ExtendDemo':
        setExtendDemoModal(true);
        break;
      case 'Activate':
        setLoadingAction('Activate');
        activateCustomer(id, {
          onSuccess: () => {
            setLoadingAction(null);
            handleSuccess('Customer activated successfully');
          },
          onError: (error) => {
            setLoadingAction(null);
            handleError(error, 'activate customer');
          },
        });
        break;
      case 'Retry':
        setLoadingAction('Retry');
        retryFailedCustomer(id, {
          onSuccess: () => {
            setLoadingAction(null);
            handleSuccess('Retry initiated successfully');
          },
          onError: (error) => {
            setLoadingAction(null);
            handleError(error, 'retry customer setup');
          },
        });
        break;
      case 'Archive':
        setArchiveConfirmation(true);
        break;
      case 'Delete':
        setLoadingAction('Delete');
        cancelSubscription(name, {
          onSuccess: () => {
            setLoadingAction(null);
            handleSuccess('Subscription cancelled successfully');
          },
          onError: (error) => {
            setLoadingAction(null);
            handleError(error, 'cancel subscription');
          },
        });
        break;
      default:
        break;
    }
  };

  const onArchive = () => {
    setLoadingAction('Archive');
    archiveCustomer(id, {
      onSuccess: () => {
        setLoadingAction(null);
        handleSuccess('Customer archived successfully');
      },
      onError: (error) => {
        setLoadingAction(null);
        handleError(error, 'archive customer');
      },
    });
  };

  return (
    <>
      <tr onClick={handleShowDetails}>
        <td className='d-flex align-items-center py-3 px-3'>{getAccountType.get(accountType)}</td>
        <td>
          <span>
            {status === 'FAILED' ? (
              <i className='icon-not-verified-fill text-red me-2' />
            ) : (
              <i className='icon-verified1-fill text-green me-2' />
            )}
            {fullDomain}
          </span>
        </td>
        <td>
          <StatusChip status={status} />
        </td>
        <td>
          {status !== 'PENDING' && accountOptions.length > 0 && (
            <div className='d-flex align-items-center'>
              <Menu
                data={accountOptions}
                onAction={(action) => handleAction(action)}
                isDisabled={!!loadingAction || syncHook.isLoading}
              />
              {(loadingAction || syncHook.isLoading) && (
                <span className='ms-2 text-muted small'>
                  <i className='icon-loading icon-spin me-1' />
                  {syncHook.isLoading
                    ? 'Syncing...'
                    : loadingAction === 'Archive'
                    ? 'Archiving...'
                    : loadingAction === 'Suspend'
                    ? 'Suspending...'
                    : loadingAction === 'Activate'
                    ? 'Activating...'
                    : loadingAction === 'Retry'
                    ? 'Retrying...'
                    : loadingAction === 'Delete'
                    ? 'Cancelling...'
                    : 'Processing...'}
                </span>
              )}
            </div>
          )}
        </td>
      </tr>
      <AddAdminModal
        isOpen={addAdminModal}
        users={users}
        id={id}
        onClose={() => {
          setAddAdminModal(false);
        }}
      />
      <ExtendDemoModal
        isOpen={extendDemoModal}
        trialEnd={trialEnd}
        id={id}
        onClose={() => {
          setExtendDemoModal(false);
        }}
      />
      <ConfirmationModal
        isOpen={archiveConfirmation}
        onAction={onArchive}
        onClose={() => {
          setArchiveConfirmation(false);
        }}
        description={`Are you sure you want to Archive "${subdomain}" account? This process cannot be undone.`}
        title={`Archive account`}
        type={'danger'}
        actionText={'Archive'}
      />
      <Toast {...toast} onClose={hideToast} />
      {syncHook.notification?.show && (
        <Toast
          show={syncHook.notification.show}
          type={syncHook.notification.loading ? 'loading' : syncHook.notification.success ? 'success' : 'error'}
          message={syncHook.notification.message}
          duration={syncHook.notification.loading ? 0 : syncHook.notification.success ? 3000 : 5000}
          onClose={() => syncHook.hideNotification()}
        />
      )}
    </>
  );
};

export default CustomerAccountsTableRow;
